import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AnalyticsTable = ({ companies, selectedMetric }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'totalSales', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedCompanies = useMemo(() => {
    const sortableCompanies = [...companies];
    if (sortConfig.key) {
      sortableCompanies.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableCompanies;
  }, [companies, sortConfig]);

  const paginatedCompanies = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedCompanies.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedCompanies, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(companies.length / itemsPerPage);

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-foreground" />
      : <Icon name="ArrowDown" size={14} className="text-foreground" />;
  };

  const getPerformanceColor = (value) => {
    if (value >= 10) return 'text-success';
    if (value >= 0) return 'text-warning';
    return 'text-error';
  };

  const getCollectionEfficiencyColor = (rate) => {
    if (rate >= 90) return 'text-success';
    if (rate >= 70) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Company Performance Analytics</h3>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm">
              <Icon name="Download" size={14} />
              Export
            </Button>
            <Button variant="ghost" size="sm">
              <Icon name="Filter" size={14} />
              Filter
            </Button>
          </div>
        </div>
      </div>

      {/* Table Container with Horizontal Scroll */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 hover:text-foreground transition-colors duration-150"
                >
                  <span>Company</span>
                  {getSortIcon('name')}
                </button>
              </th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <button
                  onClick={() => handleSort('totalSales')}
                  className="flex items-center justify-end space-x-1 hover:text-foreground transition-colors duration-150"
                >
                  <span>Total Sales</span>
                  {getSortIcon('totalSales')}
                </button>
              </th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <button
                  onClick={() => handleSort('totalCollections')}
                  className="flex items-center justify-end space-x-1 hover:text-foreground transition-colors duration-150"
                >
                  <span>Collections</span>
                  {getSortIcon('totalCollections')}
                </button>
              </th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <button
                  onClick={() => handleSort('salesGrowth')}
                  className="flex items-center justify-end space-x-1 hover:text-foreground transition-colors duration-150"
                >
                  <span>Growth Rate</span>
                  {getSortIcon('salesGrowth')}
                </button>
              </th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <button
                  onClick={() => handleSort('collectionRate')}
                  className="flex items-center justify-end space-x-1 hover:text-foreground transition-colors duration-150"
                >
                  <span>Collection Rate</span>
                  {getSortIcon('collectionRate')}
                </button>
              </th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <button
                  onClick={() => handleSort('targetAchievement')}
                  className="flex items-center justify-end space-x-1 hover:text-foreground transition-colors duration-150"
                >
                  <span>Target Achievement</span>
                  {getSortIcon('targetAchievement')}
                </button>
              </th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Outstanding
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedCompanies.map((company, index) => (
              <tr
                key={company.id}
                className={`border-b border-border hover:bg-muted/30 transition-colors duration-150 ${
                  index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                }`}
              >
                <td className="p-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                      <span className="text-xs font-medium text-accent-foreground">
                        {company.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{company.name}</div>
                      <div className="text-xs text-muted-foreground">{company.region}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className="text-sm font-medium text-foreground">
                    {formatCurrency(company.totalSales)}
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className="text-sm font-medium text-foreground">
                    {formatCurrency(company.totalCollections)}
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className={`text-sm font-medium ${getPerformanceColor(company.salesGrowth)}`}>
                    {formatPercentage(company.salesGrowth)}
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className={`text-sm font-medium ${getCollectionEfficiencyColor(company.collectionRate)}`}>
                    {company.collectionRate.toFixed(1)}%
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <div className="w-16 bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          company.targetAchievement >= 100 
                            ? 'bg-success' 
                            : company.targetAchievement >= 80 
                            ? 'bg-warning' :'bg-error'
                        }`}
                        style={{ width: `${Math.min(company.targetAchievement, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-foreground w-12 text-right">
                      {company.targetAchievement.toFixed(0)}%
                    </span>
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className="text-sm font-medium text-foreground">
                    {formatCurrency(company.outstanding)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, companies.length)} of {companies.length} companies
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <Icon name="ChevronLeft" size={14} />
                Previous
              </Button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className="w-8 h-8 p-0"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
                <Icon name="ChevronRight" size={14} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTable;